import { createContext, useContext, useEffect, useState } from 'react'
import { syncNativeWidgetData } from 'helpers/nativeWidgetSync'
import {
  DIYANET_API_BASE_URL,
  DiyanetPrayerTime,
  normalizeDiyanetPrayerTimes,
} from '../../../utils/diyanetPrayerTimes'
import {
  getLocationPrayerMethod,
  PrayerMethod,
  PrayerTimesData,
  SelectedLocation,
} from './types'

type PrayerTimesContextType = {
  method: PrayerMethod
  selectedLocation: SelectedLocation | null
  prayerTimes: PrayerTimesData | null
  loading: boolean
  error: string | null
  setLocation: (_location: SelectedLocation) => void
  refreshTimes: () => Promise<void>
}

const PrayerTimesContext = createContext<PrayerTimesContextType>({
  method: 'diyanet',
  selectedLocation: null,
  prayerTimes: null,
  loading: false,
  error: null,
  setLocation: () => {},
  refreshTimes: async () => {},
})

const getTodayString = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

const getCacheKey = (location: SelectedLocation) =>
  `prayer-times-monthly-v2-${getLocationPrayerMethod(location)}-${location.districtId}`

const isCacheCompatible = (data: PrayerTimesData, method: PrayerMethod) => {
  if (!Array.isArray(data.data)) return false
  return method === 'diyanet'
    ? data.source.toLocaleLowerCase('tr').includes('diyanet')
    : !data.source.toLocaleLowerCase('tr').includes('diyanet')
}

const readCachedTimes = (location: SelectedLocation): PrayerTimesData | null => {
  try {
    const stored = localStorage.getItem(getCacheKey(location))
    if (!stored) return null

    const data = JSON.parse(stored) as PrayerTimesData
    return isCacheCompatible(data, getLocationPrayerMethod(location)) ? data : null
  } catch {
    return null
  }
}

const buildPrayerTimesUrl = (location: SelectedLocation): string => {
  const method = getLocationPrayerMethod(location)

  if (method === 'diyanet') {
    if (!location.districtId || !/^\d+$/.test(location.districtId)) {
      throw new Error('Diyanet ilçe bilgisi eksik')
    }
    return `${DIYANET_API_BASE_URL}/vakitler/${encodeURIComponent(location.districtId)}`
  }

  let latitude = location.latitude
  let longitude = location.longitude
  if ((latitude == null || longitude == null) && location.districtId?.includes(',')) {
    const [lat, lng] = location.districtId.split(',').map(Number)
    latitude = lat
    longitude = lng
  }

  if (
    latitude == null ||
    longitude == null ||
    !isFinite(latitude) ||
    !isFinite(longitude)
  ) {
    throw new Error('Konum koordinatları eksik')
  }

  return `/api/inhouse-prayer-times?days=30&method=UmmAlQura&lat=${encodeURIComponent(latitude)}&lng=${encodeURIComponent(longitude)}`
}

const fetchPrayerTimes = async (
  location: SelectedLocation,
  signal?: AbortSignal,
): Promise<PrayerTimesData> => {
  const response = await fetch(buildPrayerTimesUrl(location), { signal })
  if (!response.ok) throw new Error('Failed to fetch prayer times')

  const rawData = await response.json()
  const method = getLocationPrayerMethod(location)
  const data =
    method === 'diyanet'
      ? normalizeDiyanetPrayerTimes(location.districtId!, rawData as DiyanetPrayerTime[])
      : (rawData as PrayerTimesData)
  if (!isCacheCompatible(data, getLocationPrayerMethod(location))) {
    throw new Error('Prayer time source did not match selected method')
  }
  return data
}

export const PrayerTimesProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedLocation, setSelectedLocationState] = useState<SelectedLocation | null>(
    null,
  )
  const [prayerTimes, setPrayerTimes] = useState<PrayerTimesData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const method = getLocationPrayerMethod(selectedLocation)

  useEffect(() => {
    const storedLocation = localStorage.getItem('prayer-times-location')
    if (!storedLocation) return

    try {
      const parsedLocation = JSON.parse(storedLocation) as SelectedLocation
      const migratedLocation = {
        ...parsedLocation,
        method: getLocationPrayerMethod(parsedLocation),
      }
      setSelectedLocationState(migratedLocation)
      localStorage.setItem('prayer-times-location', JSON.stringify(migratedLocation))
    } catch (parseError) {
      console.error('Failed to parse stored prayer location', parseError)
    }
  }, [])

  const setLocation = (location: SelectedLocation) => {
    const normalizedLocation = {
      ...location,
      method: getLocationPrayerMethod(location),
    }
    setPrayerTimes(null)
    setError(null)
    setSelectedLocationState(normalizedLocation)
    localStorage.setItem('prayer-times-location', JSON.stringify(normalizedLocation))
  }

  useEffect(() => {
    if (!selectedLocation) return

    const controller = new AbortController()
    const loadTimes = async () => {
      setLoading(true)
      setError(null)

      const cachedData = readCachedTimes(selectedLocation)
      const hasFreshData = cachedData?.data.some((day) => day.date >= getTodayString())
      if (cachedData && (hasFreshData || !navigator.onLine)) {
        setPrayerTimes(cachedData)
        setLoading(false)
        return
      }

      try {
        const data = await fetchPrayerTimes(selectedLocation, controller.signal)
        localStorage.setItem(getCacheKey(selectedLocation), JSON.stringify(data))
        setPrayerTimes(data)
      } catch (fetchError) {
        if (controller.signal.aborted) return
        console.error(fetchError)

        if (cachedData) {
          setPrayerTimes(cachedData)
        } else {
          setError('Namaz vakitleri alınamadı. Lütfen konumu tekrar seçiniz.')
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    void loadTimes()
    return () => controller.abort()
  }, [selectedLocation])

  // Send the selected location's prayer times to the Android home screen widget.
  useEffect(() => {
    if (!selectedLocation || !prayerTimes?.data.length) return

    syncNativeWidgetData({
      prayer: {
        locationName: selectedLocation.districtName,
        districtId: selectedLocation.districtId,
        days: prayerTimes.data.map((day) => ({ date: day.date, times: day.times })),
      },
    })
  }, [prayerTimes, selectedLocation])

  const refreshTimes = async () => {
    if (!selectedLocation) return
    setLoading(true)
    setError(null)

    try {
      if (!navigator.onLine) {
        const cachedData = readCachedTimes(selectedLocation)
        if (cachedData) {
          setPrayerTimes(cachedData)
          return
        }
      }

      const data = await fetchPrayerTimes(selectedLocation)
      localStorage.setItem(getCacheKey(selectedLocation), JSON.stringify(data))
      setPrayerTimes(data)
    } catch (fetchError) {
      console.error(fetchError)
      setError('Namaz vakitleri alınamadı')
    } finally {
      setLoading(false)
    }
  }

  return (
    <PrayerTimesContext.Provider
      value={{
        method,
        selectedLocation,
        prayerTimes,
        loading,
        error,
        setLocation,
        refreshTimes,
      }}
    >
      {children}
    </PrayerTimesContext.Provider>
  )
}

export const usePrayerTimes = () => useContext(PrayerTimesContext)
